import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiService } from '../services/api'
import { money, fmtDate, PayerPill, Avatar, Spinner } from '../lib/ui'

const firstOfYear = () => `${new Date().getFullYear()}-01-01`
const lastOfYear = () => `${new Date().getFullYear()}-12-31`

const grays = ['#cdd0d6', '#bcbfc6', '#abaeb6', '#9b9fa7', '#8c9098', '#7e828a', '#71757d', '#666a72']
const dow = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Not relevant for the Reports analytics page — excluded from its stats only
// (still counted everywhere else, e.g. the Balance / who-owes math).
const EXCLUDE_COMPANY = 'Allgemeine Schulden'

const ReportsPage: React.FC = () => {
  const [start, setStart] = useState(firstOfYear())
  const [end, setEnd] = useState(lastOfYear())

  const range = { start_date: start + 'T00:00:00', end_date: end + 'T23:59:59' }

  const { data: rawReceipts = [], isLoading: l2 } = useQuery({ queryKey: ['receipts', range], queryFn: () => apiService.getReceipts({ ...range, limit: 1000 }) })
  const { data: payments = [], isLoading: l3 } = useQuery({ queryKey: ['payments', range], queryFn: () => apiService.getPayments(range) })

  // Drop entries not relevant to this page so they don't skew any report stat.
  const receipts = rawReceipts.filter((r) => r.company !== EXCLUDE_COMPANY)

  const loading = l2 || l3
  const total = receipts.reduce((s, r) => s + r.total_amount, 0)
  const amts = receipts.map((r) => r.total_amount)
  const days = Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 864e5) + 1)
  const dSpent = receipts.filter((r) => r.payer === 'David').reduce((s, r) => s + r.total_amount, 0)
  const tSpent = receipts.filter((r) => r.payer === 'Timna').reduce((s, r) => s + r.total_amount, 0)

  const byStore: Record<string, number> = {}
  receipts.forEach((r) => { byStore[r.company] = (byStore[r.company] || 0) + r.total_amount })
  const storeArr = Object.entries(byStore).sort((a, b) => b[1] - a[1])
  const maxStore = Math.max(...storeArr.map((s) => s[1]), 1)

  const dowSum: Record<string, number> = {}
  receipts.forEach((r) => { const d = (new Date(r.date).getDay() + 6) % 7; dowSum[dow[d]] = (dowSum[dow[d]] || 0) + r.total_amount })
  const maxDow = Math.max(...dow.map((d) => dowSum[d] || 0), 1)

  return (
    <>
      <h2 className="page">Reports</h2>
      <p className="sub">Spending analytics for any date range.</p>

      <div className="glass-card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}><label>From</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}><label>To</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
      </div>

      {loading ? (
        <div className="center-pad"><Spinner /></div>
      ) : (
        <>
          <div className="glass-card" style={{ marginBottom: 16 }}>
            <div className="section-title" style={{ marginTop: 0 }}>Spending overview</div>
            <div className="stat-grid">
              <div className="stat glass"><div className="lbl">Total spent</div><div className="num">{money(total)}</div><div className="tiny muted">{receipts.length} receipts</div></div>
              <div className="stat glass"><div className="lbl">Avg / receipt</div><div className="num" style={{ color: 'var(--david)' }}>{money(receipts.length ? total / receipts.length : 0)}</div></div>
              <div className="stat glass"><div className="lbl">Avg / day</div><div className="num">{money(total / days)}</div></div>
              <div className="stat glass"><div className="lbl">Largest</div><div className="num">{money(amts.length ? Math.max(...amts) : 0)}</div></div>
              <div className="stat glass"><div className="lbl">Smallest</div><div className="num">{money(amts.length ? Math.min(...amts) : 0)}</div></div>
            </div>
            <div className="split-row" style={{ marginTop: 14 }}>
              <div className="who-card glass"><Avatar who="David" /><div className="tiny muted">David spent</div><div className="val" style={{ color: 'var(--david)' }}>{money(dSpent)}</div></div>
              <div className="who-card glass"><Avatar who="Timna" /><div className="tiny muted">Timna spent</div><div className="val" style={{ color: 'var(--timna)' }}>{money(tSpent)}</div></div>
            </div>
          </div>

          {payments.length > 0 && (
            <div className="glass-card" style={{ marginBottom: 16 }}>
              <div className="section-title" style={{ marginTop: 0 }}>Payment history</div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Ticket</th><th>Date</th><th>Month</th><th>By</th><th>Amount</th><th>Notes</th></tr></thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td><span className="pill pill-track">{p.tracking_number}</span></td>
                        <td>{fmtDate(p.payment_date)}</td>
                        <td>{p.month_id}</td>
                        <td><PayerPill payer={p.payer} /></td>
                        <td style={{ color: 'var(--david)', fontWeight: 700 }}>{money(p.amount)}</td>
                        <td className="muted">{p.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="glass-card" style={{ marginBottom: 16 }}>
            <div className="section-title" style={{ marginTop: 0 }}>Top stores</div>
            {storeArr.length ? storeArr.slice(0, 8).map(([s, v], i) => (
              <div key={s} className="bar-row">
                <div className="bar-top"><span>{s}</span><span className="muted">{money(v)}</span></div>
                <div className="bar-track"><div className="bar-fill" style={{ width: `${(v / maxStore) * 100}%`, background: grays[i % grays.length] }} /></div>
              </div>
            )) : <div className="muted tiny">No data in range.</div>}
          </div>

          <div className="glass-card">
            <div className="section-title" style={{ marginTop: 0 }}>Spending by day of week</div>
            {dow.map((d) => {
              const v = dowSum[d] || 0
              return (
                <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 9 }}>
                  <div style={{ width: 38 }} className="tiny muted">{d}</div>
                  <div className="bar-track" style={{ flex: 1, height: 18, position: 'relative' }}>
                    <div className="bar-fill" style={{ width: `${(v / maxDow) * 100}%` }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600 }}>{money(v)}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}

export default ReportsPage
